import React, { useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import {
  UserSignature,
  userSignatureService,
} from '../../services/userSignatureService';

export const UserSignaturePage: React.FC = () => {
  const { addToast } = useToast();
  const [signature, setSignature] = useState<UserSignature | null>(null);
  const [messageSignature, setMessageSignature] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSignature = async () => {
      try
      {
        const data = await userSignatureService.get();
        setSignature(data);
        setMessageSignature(data.messageSignature);
      }
      catch
      {
        addToast('Erro ao carregar assinatura', 'error');
      }
      finally
      {
        setLoading(false);
      }
    };

    void loadSignature();
  }, [addToast]);

  useEffect(() => {
    if (!image)
    {
      setPreviewUrl(signature?.imageUrl ?? null);
      return;
    }

    const objectUrl = URL.createObjectURL(image);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image, signature?.imageUrl]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    setImage(selected);
    setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setImage(null);
    setPreviewUrl(null);
    setRemoveImage(true);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);

    try
    {
      const updated = await userSignatureService.update(
        messageSignature,
        image,
        removeImage,
      );
      setSignature(updated);
      setMessageSignature(updated.messageSignature);
      setImage(null);
      setRemoveImage(false);
      addToast('Assinatura salva com sucesso', 'success');
    }
    catch
    {
      addToast('Erro ao salvar assinatura', 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Carregando assinatura...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Assinatura de e-mail</h1>
        <p className="text-gray-600 mt-1">
          Configure o texto e a imagem que aparecerão nas respostas enviadas pelo Atendimento.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <section className="bg-white border border-gray-200 rounded-lg shadow p-6">
          <label htmlFor="user-signature" className="block text-sm font-medium text-gray-700 mb-2">
            Texto da assinatura
          </label>
          <textarea
            id="user-signature"
            rows={8}
            value={messageSignature}
            onChange={(event) => setMessageSignature(event.target.value)}
            placeholder={'Atenciosamente,\nSeu nome\nBaruk Know'}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-900 focus:border-blue-900"
          />
          <p className="text-xs text-gray-500 mt-2">
            A assinatura é adicionada ao final da resposta quando “Incluir assinatura” estiver habilitado.
          </p>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">Imagem do rodapé</h2>
          <p className="text-sm text-gray-600 mt-1 mb-4">
            A imagem será enviada incorporada ao e-mail, sem depender de uma URL pública.
          </p>
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImageChange}
            className="block w-full text-sm text-gray-600"
          />
          {previewUrl && (
            <div className="mt-4 flex items-start gap-4">
              <img src={previewUrl} alt="Prévia da assinatura" className="max-h-24 max-w-full object-contain" />
              <Button type="button" variant="dangerOutline" size="sm" onClick={handleRemoveImage}>
                Remover imagem
              </Button>
            </div>
          )}
        </section>

        <Button type="submit" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar assinatura'}
        </Button>
      </form>
    </div>
  );
};
